import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faKey, faTags } from '@fortawesome/pro-solid-svg-icons'
import Avatar from 'design-system/avatar'
import { AvatarSizes } from 'design-system/avatar/src/resources/Avatar.enum'
import Loader from 'components/ui/loader/Loader'
import ProfileModal from 'components/reusables/profileModal'
import Wrapper from 'components/reusables/wrapper'
import { resolveUserAvatarUrl } from 'helpers/avatar.helper'
import { useAccess } from 'contexts/AccessContext'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/participants.action'
import PermissionsModal from './permissionsModal'
import styles from './ParticipantHeaderCard.module.scss'

interface IProps {
  userId: number
}

/**
 * Header Figma de ficha participante (admin): datos + Ver permisos / Ver perfiles LMS.
 */
export const ParticipantHeaderCard = ({ userId }: IProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isAdmin } = useAccess()
  const hasFullAccess = isAdmin()
  const { participant, loading } = useSelector(
    ({ participants }: IMainState) => participants
  )
  const [showProfiles, setShowProfiles] = useState(false)
  const [showPermissions, setShowPermissions] = useState(false)

  useEffect(() => {
    dispatch({
      type: fromActions.PARTICIPANTS_PROFILES_GET,
      payload: userId,
    })
  }, [dispatch, userId])

  if (loading && !participant?.id) {
    return <Loader overlay color />
  }

  const {
    fullName,
    name,
    lastname,
    position,
    area,
    identifier,
    email,
    avatar,
    directManager,
  } = participant || {}

  const displayName =
    fullName || [name, lastname].filter(Boolean).join(' ') || '—'
  const areaName = area?.name
  const subtitle = [position, areaName].filter(Boolean).join(' | ')
  const contact = [identifier, email].filter(Boolean).join(' | ')
  const manager = directManager
  const managerLabel = manager
    ? [
        manager.fullName ||
          [manager.name, manager.lastname].filter(Boolean).join(' '),
        manager.position ? `(${manager.position})` : null,
      ]
        .filter(Boolean)
        .join(' ')
    : null

  return (
    <>
      <div className={styles.headerCard}>
        <Wrapper shadow>
          <div className={styles.card}>
            <div className={styles.left}>
              <Avatar
                sizes={AvatarSizes.XExtraLarge}
                className={styles.avatar}
                src={resolveUserAvatarUrl(avatar)}
                useOriginalImage
              />
              <div className={styles.info}>
                <h1 className={styles.name}>{displayName}</h1>
                {subtitle && <p className={styles.meta}>{subtitle}</p>}
                {contact && <p className={styles.meta}>{contact}</p>}
                {managerLabel && (
                  <p className={styles.manager}>
                    <strong>
                      {t('configuration.userProfile.header.directManager', {
                        defaultValue: 'Jefe directo:',
                      })}
                    </strong>{' '}
                    {managerLabel}
                  </p>
                )}
              </div>
            </div>
            {hasFullAccess && (
              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.actionLink}
                  onClick={() => setShowPermissions(true)}
                >
                  <FontAwesomeIcon icon={faKey} />
                  {t('configuration.userProfile.header.viewPermissions', {
                    defaultValue: 'Ver permisos',
                  })}
                </button>
                <button
                  type="button"
                  className={styles.actionLink}
                  onClick={() => setShowProfiles(true)}
                >
                  <FontAwesomeIcon icon={faTags} />
                  {t('configuration.userProfile.header.viewLmsProfiles', {
                    defaultValue: 'Ver perfiles LMS asignados',
                  })}
                </button>
              </div>
            )}
          </div>
        </Wrapper>
      </div>
      {showPermissions && (
        <PermissionsModal
          userId={userId}
          displayName={displayName}
          subtitle={subtitle || undefined}
          avatar={avatar}
          onClose={() => setShowPermissions(false)}
        />
      )}
      {showProfiles && (
        <ProfileModal
          resourceId={userId}
          resourceType="user"
          close={() => setShowProfiles(false)}
        />
      )}
    </>
  )
}

export default ParticipantHeaderCard
